import {
  getRecurringBreaks,
  getTimeOff,
  getWeeklyAvailabilitySchedule,
  type RecurringBreak,
  type TimeOff,
  type WeeklyAvailabilitySchedule,
} from '@/lib/api/availability';
import { getAppointments, mapAppointmentRowsFromApi } from '@/lib/api/appointments';
import type { Appointment } from '@/types';

export interface ScheduleAvailabilityContext {
  schedule: WeeklyAvailabilitySchedule;
  breaks: RecurringBreak[];
  timeOff: TimeOff[];
  appointments: Appointment[];
}

export type FetchScheduleAvailabilityContextOptions = {
  /** When false, skips GET /appointments (reschedule uses available-slots per day instead). */
  includeAppointments?: boolean;
};

const inflightContextRequests = new Map<string, Promise<ScheduleAvailabilityContext>>();

/** Loads nutritionist availability inputs used to compute bookable days and time slots. */
export async function fetchScheduleAvailabilityContext(
  rangeStart: Date,
  rangeEnd: Date,
  options?: FetchScheduleAvailabilityContextOptions,
): Promise<ScheduleAvailabilityContext> {
  const includeAppointments = options?.includeAppointments !== false;
  const key = `${rangeStart.toISOString()}|${rangeEnd.toISOString()}|appointments=${includeAppointments}`;
  const inflight = inflightContextRequests.get(key);
  if (inflight) return inflight;

  const request = (async (): Promise<ScheduleAvailabilityContext> => {
    const [schedule, breaksResult, timeOffResult, appointmentsResult] = await Promise.all([
      getWeeklyAvailabilitySchedule(),
      getRecurringBreaks().catch(() => [] as RecurringBreak[]),
      getTimeOff().catch(() => [] as TimeOff[]),
      includeAppointments
        ? getAppointments({
            page: 1,
            limit: 1000,
            startDate: rangeStart.toISOString(),
            endDate: rangeEnd.toISOString(),
          }).catch(() => ({ appointments: [], count: 0 }))
        : Promise.resolve({ appointments: [], count: 0 }),
    ]);

    return {
      schedule,
      breaks: breaksResult,
      timeOff: timeOffResult,
      appointments: includeAppointments
        ? mapAppointmentRowsFromApi(appointmentsResult.appointments as unknown[])
        : [],
    };
  })();

  inflightContextRequests.set(key, request);

  try {
    return await request;
  } finally {
    inflightContextRequests.delete(key);
  }
}
